import { memo, useMemo, useRef, useState } from 'react'
import {
  type LayoutChangeEvent,
  Text as RNText,
  StyleSheet,
  type TextStyle,
  View,
} from 'react-native'
import { Gesture, GestureDetector } from 'react-native-gesture-handler'
import Animated, {
  FadeIn,
  FadeOut,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated'

import { useHapticFeedback } from '../haptics/HapticsContext'
import { Text, useTheme } from '../theme/restyle'
import { flatTextStyle } from '../theme/textStyle'
import type { FunkitTheme } from '../theme/toRestyleTheme'

export interface FiatCountryIndexRailProps {
  /** The headings the list draws, in the order it draws them. */
  keys: readonly string[]
  /** The heading the list is scrolled to, marked when no finger is down. */
  active?: string
  /**
   * Where down the rail the finger is: which heading it is over, and how far
   * through that heading's letter it sits. Called for every touch, not only
   * when the letter changes, so the list can follow the finger. The index
   * rather than the letter, because the caller wants the next heading too.
   */
  onScrub: (at: number, within: number) => void
}

/**
 * The letters down the right edge, dragged through to reach a heading — what
 * a contacts list is read by, and the only way 245 countries are reachable
 * without a search term.
 *
 * Driven by a gesture-handler pan, which is what the sheet's own pan-to-close
 * is: on the responder system the sheet took the drag over after the first
 * letter, and the scrub stopped with its peek still drawn. Sharing one gesture
 * system makes the two compete, and the rail wins by activating on touch-down
 * where the sheet's pan waits for movement.
 */
export const FiatCountryIndexRail = memo(function FiatCountryIndexRail({
  keys,
  active,
  onScrub,
}: FiatCountryIndexRailProps) {
  const theme = useTheme()
  const haptic = useHapticFeedback()
  // Where the letters sit in the band — the figures the touch is read with and
  // the peek is placed off. Set once, when the strip is laid out.
  const [strip, setStrip] = useState<{ pitch: number; inset: number }>({
    pitch: RAIL.maxPitch,
    inset: 0,
  })
  const [peek, setPeek] = useState<string>()
  // The peek rides a shared value rather than React state: it follows the
  // finger exactly, and the spring carries it between touches while the JS
  // thread is busy scrolling the list.
  const peekY = useSharedValue(0)
  const lastKey = useRef<string | undefined>(undefined)

  // `y` is the touch's place in the strip itself, which is the frame the
  // letters are laid out in — nothing has to be measured to read it.
  const scrub = (y: number) => {
    const { inset, pitch } = strip
    // Where the finger is in letters, fraction and all: the list is scrolled
    // to the fraction, so it tracks the finger rather than stepping a letter
    // at a time.
    const through = Math.min(Math.max((y - inset) / pitch, 0), keys.length)
    const at = Math.min(Math.floor(through), keys.length - 1)
    const key = keys[at]
    if (key === undefined) {
      return
    }
    onScrub(at, through - at)
    // The bubble follows the pixel; the letter inside it follows the letter.
    peekY.value = lastKey.current === undefined ? y : withSpring(y, PEEK_SPRING)
    if (key === lastKey.current) {
      return
    }
    lastKey.current = key
    setPeek(key)
    haptic()
  }
  // The gesture is built once; its callbacks reach the current render's scrub
  // through this, or a drag would scrub against the first render's letters.
  const latest = useRef(scrub)
  latest.current = scrub
  const release = () => {
    lastKey.current = undefined
    setPeek(undefined)
  }
  const pan = useMemo(
    () =>
      Gesture.Pan()
        // Activates on touch-down rather than after a drag, so the sheet's own
        // pan never gets to claim the gesture first.
        .minDistance(0)
        // A drag that wanders off the strip is still a scrub; the letter is
        // read from where it is vertically.
        .shouldCancelWhenOutside(false)
        // The callbacks touch React state and the haptics module, neither of
        // which is a worklet.
        .runOnJS(true)
        .onBegin((event) => latest.current(event.y))
        .onUpdate((event) => latest.current(event.y))
        // Finalize, not end: a gesture that is cancelled has no end, and a
        // peek left drawn says the finger is still down.
        .onFinalize(release),
    [],
  )

  const onLayout = ({ nativeEvent }: LayoutChangeEvent) => {
    const { height } = nativeEvent.layout
    const next = Math.min(RAIL.maxPitch, height / Math.max(keys.length, 1))
    // The letters are centred in the band, so the first one starts below its
    // top — a touch measured from the top alone reads several letters early.
    setStrip({
      pitch: next,
      inset: Math.max(0, (height - next * keys.length) / 2),
    })
  }

  const peekStyle = useAnimatedStyle(() => {
    // Spelled out because the plugin workletizes by the hook's name, and
    // esbuild renames this one in the bundle (see AmountDisplay).
    'worklet'
    return { transform: [{ translateY: peekY.value - RAIL.peekSize / 2 }] }
  })
  // A finger down outranks the scroll position: the list is still catching up
  // to it, and a mark that lags the touch reads as the wrong letter.
  const marked = peek ?? active
  const letterStyles = useMemo(
    () => letterPalette(theme, strip.pitch),
    [theme, strip.pitch],
  )

  return (
    // Wider than the strip so the peek fits inside it; only the strip takes
    // touches, or a scroll started beside the letters would be caught here.
    <View pointerEvents="box-none" style={styles.root}>
      {peek === undefined ? null : (
        <Animated.View
          pointerEvents="none"
          entering={FadeIn.duration(PEEK_IN)}
          exiting={FadeOut.duration(PEEK_OUT)}
          style={[
            styles.peek,
            { backgroundColor: theme.content.elements.ghosted },
            peekStyle,
          ]}
        >
          {/* Keyed on the letter, so the one arriving fades up over the one
              leaving rather than replacing it outright. */}
          <Animated.View
            key={peek}
            entering={FadeIn.duration(PEEK_LETTER)}
            exiting={FadeOut.duration(PEEK_LETTER)}
            style={styles.peekLetter}
          >
            <Text
              weight="medium"
              style={[
                styles.peekText,
                { color: theme.content.elements.primary },
              ]}
            >
              {peek}
            </Text>
          </Animated.View>
        </Animated.View>
      )}

      <GestureDetector gesture={pan}>
        <View
          onLayout={onLayout}
          // A scroll accelerator, not a control: every country is already
          // reachable by name, and 27 letters read aloud is noise.
          importantForAccessibility="no-hide-descendants"
          accessibilityElementsHidden
          style={styles.strip}
        >
          <View pointerEvents="none" style={styles.letters}>
            {keys.map((key) => (
              <RailLetter
                key={key}
                letter={key}
                style={
                  key === marked ? letterStyles.marked : letterStyles.plain
                }
              />
            ))}
          </View>
        </View>
      </GestureDetector>
    </View>
  )
})

/**
 * The two ways a letter is drawn, built once for the strip.
 *
 * Drawn through Restyle each letter would resolve its style props and read the
 * theme on every render, 27 times over, for a mark that moves one of them.
 */
function letterPalette(theme: FunkitTheme, pitch: number) {
  const metrics: TextStyle = {
    height: pitch,
    lineHeight: pitch,
    fontSize: Math.min(RAIL.fontSize, pitch - 1),
  }
  const base = [
    ...flatTextStyle(theme, { weight: 'medium' }),
    styles.letter,
    metrics,
  ]
  return {
    marked: [...base, { color: theme.brand.primary }],
    plain: [...base, { color: theme.content.elements.secondary }],
  }
}

/** One letter, redrawn only when the mark reaches or leaves it. */
const RailLetter = memo(function RailLetter({
  letter,
  style,
}: {
  letter: string
  style: TextStyle[]
}) {
  return <RNText style={style}>{letter}</RNText>
})

/** Carries the bubble between touches, and smooths a finger the list stalls. */
const PEEK_SPRING = { damping: 22, stiffness: 320, mass: 0.6 } as const
const PEEK_IN = 120
const PEEK_OUT = 160
const PEEK_LETTER = 140

const RAIL = {
  /** What iOS gives a letter when the whole alphabet fits. */
  maxPitch: 14,
  fontSize: 11,
  /** The strip iOS gives the index on a phone (2861:12812). */
  width: 24,
  /** The peek bubble, and the gap it keeps off the letters. */
  peekSize: 44,
  peekGap: 8,
} as const

const styles = StyleSheet.create({
  root: {
    flex: 1,
    width: RAIL.width + RAIL.peekGap + RAIL.peekSize,
    alignItems: 'flex-end',
  },
  // The band it scrubs is the host's to place; the strip fills it.
  strip: {
    flex: 1,
    width: RAIL.width,
    alignItems: 'center',
    justifyContent: 'center',
  },
  letters: { width: '100%', alignItems: 'center' },
  peek: {
    position: 'absolute',
    right: RAIL.width + RAIL.peekGap,
    top: 0,
    width: RAIL.peekSize,
    height: RAIL.peekSize,
    borderRadius: RAIL.peekSize / 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // The letters are stacked, so a cross-fade shows both in the same place.
  peekLetter: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  peekText: { fontSize: 20 },
  letter: { textAlign: 'center', width: '100%' },
})
